import { useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { DefaultValues, FieldValues, useForm } from 'react-hook-form'

import { TabFiltersProps } from '../filter.types'
import { useFilterMetadata } from './useFilterMetadata'
import { useInitialValues } from './useInitialValues'
import { buildFieldBlocks } from '../utils/buildFieldBlocks'

interface UseFilterFormParams<T extends FieldValues, Q> {
  filtersConfig: TabFiltersProps<T, Q>['filtersConfig']
  onFinish: (data: T) => void
  resetFilters: () => void
  initValues?: DefaultValues<T>
}

export function useFilterForm<T extends FieldValues, Q>({
  filtersConfig,
  onFinish,
  resetFilters,
  initValues,
}: UseFilterFormParams<T, Q>) {
  const searchParams = useSearchParams()
  const { queryParams } = useInitialValues(searchParams)

  const isTabFilter = initValues !== undefined

  const form = useForm<T>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      ...filtersConfig.initialValues,
      ...(isTabFilter ? {} : queryParams),
      ...initValues,
    },
  })

  const { filterData, loadFilterMetadata, isLoading } = useFilterMetadata(filtersConfig, form)

  const fieldOptions = useMemo(() => filterData?.options ?? {}, [filterData?.options])
  const fields = useMemo(() => filtersConfig.fields(fieldOptions), [filtersConfig, fieldOptions])
  const fieldBlocks = useMemo(() => buildFieldBlocks(fields), [fields])

  const sectionNames = useMemo(
    () => fieldBlocks.filter((b) => b.section !== null).map((b) => b.section as string),
    [fieldBlocks],
  )

  const isFirstMetadataLoad = !filterData
  const showSkeletons = isLoading && isFirstMetadataLoad

  const onSubmit = useCallback(
    (data: T) => {
      onFinish(data)
      loadFilterMetadata()
    },
    [onFinish, loadFilterMetadata],
  )

  const resetFormFilters = useCallback(() => {
    form.reset({
      saved_filters: '',
      ...filtersConfig.initialValues,
    })
    resetFilters()
    loadFilterMetadata()
  }, [filtersConfig.initialValues, form, resetFilters, loadFilterMetadata])

  return {
    form,
    fields,
    fieldBlocks,
    sectionNames,
    showSkeletons,
    isLoading,
    filterData,
    onSubmit,
    resetFormFilters,
    loadFilterMetadata,
  }
}
